import React, { useState, useEffect } from 'react';
import { shippingDiscountAPI } from '../../utils/api';
import { LoadingSpinner } from '../../components/common';

const EMPTY = {
  name: '', code: '', description: '',
  discount_type: 'free', discount_value: 0,
  min_order_amount: 0, max_uses: '', valid_from: '', valid_until: '', is_active: 1,
};

const TYPE_LABELS = { free: '🆓 Free Shipping', percentage: '% Percentage Off', fixed: '$ Fixed Amount Off' };

const AdminShippingDiscounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    shippingDiscountAPI.getAll().then(r => setDiscounts(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const startEdit = (d) => {
    setEditId(d.id);
    setForm({
      name: d.name, code: d.code || '', description: d.description || '',
      discount_type: d.discount_type, discount_value: d.discount_value,
      min_order_amount: d.min_order_amount, max_uses: d.max_uses || '',
      valid_from: d.valid_from ? d.valid_from.slice(0, 10) : '',
      valid_until: d.valid_until ? d.valid_until.slice(0, 10) : '',
      is_active: d.is_active,
    });
    setError('');
  };

  const cancelEdit = () => { setEditId(null); setForm(EMPTY); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.discount_type) { setError('Name and discount type are required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value) || 0,
        min_order_amount: parseFloat(form.min_order_amount) || 0,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
      };
      if (editId) {
        await shippingDiscountAPI.update(editId, payload);
      } else {
        await shippingDiscountAPI.create(payload);
      }
      cancelEdit();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete shipping discount "${name}"?`)) return;
    await shippingDiscountAPI.remove(id);
    load();
  };

  const toggleActive = async (d) => {
    await shippingDiscountAPI.update(d.id, { is_active: d.is_active === 1 ? 0 : 1 });
    load();
  };

  const formatDiscount = (d) => {
    if (d.discount_type === 'free') return 'Free Shipping';
    if (d.discount_type === 'percentage') return `${d.discount_value}% off shipping`;
    return `$${parseFloat(d.discount_value).toFixed(2)} off shipping`;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

  return (
    <div>
      <div className="page-header">
        <h1>🚚 Shipping Discounts</h1>
        <p>Create promo codes and rules to discount shipping at checkout</p>
      </div>

      {/* Form */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontWeight: 700, marginBottom: '1rem' }}>{editId ? 'Edit Discount' : '+ Create Shipping Discount'}</h2>
        {error && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="grid-2" style={{ gap: '0.75rem' }}>
            <div className="form-group">
              <label className="form-label">Discount Name *</label>
              <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Free Shipping Weekend" />
            </div>
            <div className="form-group">
              <label className="form-label">Promo Code</label>
              <input className="form-input" value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder="FREESHIP (leave blank = auto-apply)" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Short description shown to customers" />
          </div>

          <div className="grid-3" style={{ gap: '0.75rem' }}>
            <div className="form-group">
              <label className="form-label">Discount Type *</label>
              <select className="form-input" value={form.discount_type} onChange={e => set('discount_type', e.target.value)}>
                {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {form.discount_type !== 'free' && (
              <div className="form-group">
                <label className="form-label">{form.discount_type === 'percentage' ? 'Percentage (%)' : 'Fixed Amount ($)'}</label>
                <input className="form-input" type="number" min="0" step="0.01" value={form.discount_value} onChange={e => set('discount_value', e.target.value)} />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Min Order Amount ($)</label>
              <input className="form-input" type="number" min="0" step="0.01" value={form.min_order_amount} onChange={e => set('min_order_amount', e.target.value)} />
            </div>
          </div>

          <div className="grid-3" style={{ gap: '0.75rem' }}>
            <div className="form-group">
              <label className="form-label">Max Uses (blank = unlimited)</label>
              <input className="form-input" type="number" min="1" value={form.max_uses} onChange={e => set('max_uses', e.target.value)} placeholder="100" />
            </div>
            <div className="form-group">
              <label className="form-label">Valid From</label>
              <input className="form-input" type="date" value={form.valid_from} onChange={e => set('valid_from', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Valid Until</label>
              <input className="form-input" type="date" value={form.valid_until} onChange={e => set('valid_until', e.target.value)} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : (editId ? 'Update' : 'Create')}</button>
            {editId && <button className="btn btn-outline" type="button" onClick={cancelEdit}>Cancel</button>}
          </div>
        </form>
      </div>

      {/* Table */}
      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Min Order</th>
                  <th>Uses</th>
                  <th>Validity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {discounts.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: '#6b7280', padding: '1.5rem' }}>No shipping discounts yet</td></tr>
                )}
                {discounts.map(d => (
                  <tr key={d.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{d.name}</div>
                      {d.description && <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{d.description}</div>}
                    </td>
                    <td>
                      {d.code
                        ? <span style={{ fontFamily: 'monospace', fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', padding: '2px 7px', borderRadius: 4 }}>{d.code}</span>
                        : <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>Auto-apply</span>}
                    </td>
                    <td>{formatDiscount(d)}</td>
                    <td>{parseFloat(d.min_order_amount) > 0 ? `$${parseFloat(d.min_order_amount).toFixed(2)}` : '—'}</td>
                    <td>{d.uses_count}{d.max_uses ? ` / ${d.max_uses}` : ''}</td>
                    <td style={{ fontSize: '0.8rem' }}>
                      {formatDate(d.valid_from)} – {formatDate(d.valid_until)}
                    </td>
                    <td>
                      <button
                        className={`badge ${d.is_active === 1 ? 'badge-success' : 'badge-danger'}`}
                        style={{ cursor: 'pointer', border: 'none', background: 'transparent' }}
                        onClick={() => toggleActive(d)}
                        title="Click to toggle"
                      >
                        {d.is_active === 1 ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn btn-sm btn-outline" onClick={() => startEdit(d)}>Edit</button>
                        <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#dc2626' }} onClick={() => handleDelete(d.id, d.name)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShippingDiscounts;
